import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AssessmentOutlined,
  Close,
  DownloadOutlined,
  FilterAltOutlined,
  RestartAlt,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { usePdfSettings } from "../../../layouts/PdfSettingsContext";
import { useBranch } from "../../../../components/layouts/BranchContext";

/* =========================================================
   API
========================================================= */

const BASE_URL =
  "https://wims.revisetoexcel.com/api";

const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

API.interceptors.request.use((config) => {
  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token");

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization =
      token.startsWith("Bearer ")
        ? token
        : `Bearer ${token}`;
  }

  return config;
});

/* =========================================================
   REPORTS
========================================================= */

const REPORTS = [
  {
    key: "employees",
    label: "Employee Report",
    api: "/hr/reports/employees/",
  },
  {
    key: "leaves",
    label: "Leave Report",
    api: "/hr/reports/leaves/",
  },
  {
    key: "leave-balance",
    label: "Leave Balance Report",
    api: "/hr/reports/leaves/",
  },
  {
    key: "department-leave",
    label: "Department-wise Leave",
    api: "/hr/reports/leaves/",
  },
  {
    key: "monthly-payroll",
    label: "Monthly Payroll",
    api: "/hr/reports/payroll/",
  },
  {
    key: "employee-payroll",
    label: "Employee Payroll",
    api: "/hr/reports/payroll/",
  },
  {
    key: "department-payroll",
    label: "Department-wise Payroll",
    api: "/hr/reports/payroll/",
  },
];

/* =========================================================
   EXACT API RESPONSE COLUMNS
========================================================= */

const REPORT_COLUMNS = {
  employees: [
    "employee_code",
    "name",
    "organization_name",
    "branch_name",
    "department_name",
    "designation_name",
    "date_of_joining",
    "gender",
    "mobile",
    "phone",
    "email",
    "address",
    "salary",
    "employment_status",
    "is_active",
  ],

  leaves: [
    "employee_code",
    "employee_name",
    "organization_name",
    "branch_name",
    "department_name",
    "designation_name",
    "leave_type_name",
    "from_date",
    "to_date",
    "total_days",
    "reason",
    "status",
    "applied_date",
    "reviewed_at",
    "rejection_reason",
    "is_active",
  ],

  "leave-balance": [
    "employee_code",
    "employee_name",
    "department_name",
    "leave_type_name",
    "year",
    "allocated_days",
    "used_days",
    "remaining_days",
    "is_active",
  ],

  "department-leave": [
    "department_name",
    "total_applications",
    "pending_applications",
    "approved_applications",
    "rejected_applications",
    "cancelled_applications",
    "total_days",
    "approved_days",
  ],

  "monthly-payroll": [
    "year",
    "month",
    "employee_count",
    "total_gross_salary",
    "total_deduction",
    "total_attendance_deduction",
    "total_net_salary",
    "total_working_days",
    "total_present_days",
    "total_absent_days",
    "total_leave_days",
    "total_half_days",
    "paid_count",
    "pending_count",
  ],

  "employee-payroll": [
    "employee",
    "employee_code",
    "employee_name",
    "department_name",
    "designation_name",
    "payroll_count",
    "total_gross_salary",
    "total_deduction",
    "total_attendance_deduction",
    "total_net_salary",
    "total_working_days",
    "total_present_days",
    "total_absent_days",
    "total_leave_days",
    "total_half_days",
    "paid_count",
    "pending_count",
  ],

  "department-payroll": [
    "department",
    "department_name",
    "employee_count",
    "payroll_count",
    "total_gross_salary",
    "total_deduction",
    "total_attendance_deduction",
    "total_net_salary",
    "total_working_days",
    "total_present_days",
    "total_absent_days",
    "total_leave_days",
    "total_half_days",
    "paid_count",
    "pending_count",
  ],
};

/* =========================================================
   PDF COLUMNS
   ONLY NECESSARY COLUMNS FOR A4 LANDSCAPE
========================================================= */

const REPORT_PDF_COLUMNS = {
  employees: [
    "employee_code",
    "name",
    "department_name",
    "designation_name",
    "date_of_joining",
    "mobile",
    "email",
    "employment_status",
  ],

  leaves: [
    "employee_code",
    "employee_name",
    "leave_type_name",
    "from_date",
    "to_date",
    "total_days",
    "status",
    "reason",
  ],

  "leave-balance": [
    "employee_code",
    "employee_name",
    "leave_type_name",
    "allocated_days",
    "used_days",
    "remaining_days",
    "year",
  ],

  "department-leave": [
    "department_name",
    "total_applications",
    "pending_applications",
    "approved_applications",
    "rejected_applications",
    "total_days",
    "approved_days",
  ],

  "monthly-payroll": [
    "year",
    "month",
    "employee_count",
    "total_gross_salary",
    "total_deduction",
    "total_net_salary",
    "total_working_days",
    "total_leave_days",
  ],

  "employee-payroll": [
    "employee_code",
    "employee_name",
    "department_name",
    "designation_name",
    "total_gross_salary",
    "total_deduction",
    "total_net_salary",
    "total_working_days",
    "total_leave_days",
  ],

  "department-payroll": [
    "department_name",
    "employee_count",
    "payroll_count",
    "total_gross_salary",
    "total_deduction",
    "total_net_salary",
    "total_working_days",
    "total_leave_days",
  ],
};

/* =========================================================
   HELPERS
========================================================= */

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.results)) {
    return value.results;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  if (Array.isArray(value?.rows)) {
    return value.rows;
  }

  return [];
};

const getUser = () => {
  for (const key of [
    "user",
    "userData",
    "currentUser",
  ]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) {
        return JSON.parse(raw);
      }
    } catch {}
  }

  return {};
};

const getId = (x) =>
  x?.id ??
  x?.value ??
  x?.employee_id ??
  x?.organization_id ??
  x?.branch_id ??
  x?.department_id ??
  x?.designation_id ??
  x?.leave_type_id;

const getName = (
  x,
  fallback = "—"
) =>
  x?.name ||
  x?.full_name ||
  x?.employee_name ||
  x?.organization_name ||
  x?.branch_name ||
  x?.department_name ||
  x?.designation_name ||
  x?.leave_type_name ||
  x?.label ||
  x?.title ||
  fallback;

const labelize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) =>
      c.toUpperCase()
    );

const formatValue = (v) => {
  if (
    v === null ||
    v === undefined ||
    v === ""
  ) {
    return "—";
  }

  if (typeof v === "boolean") {
    return v ? "Yes" : "No";
  }

  if (Array.isArray(v)) {
    return v
      .map(formatValue)
      .join(", ");
  }

  if (typeof v === "object") {
    return Object.entries(v)
      .map(
        ([k, x]) =>
          `${k}: ${formatValue(x)}`
      )
      .join(" | ");
  }

  return String(v);
};

const formatDate = (v) => {
  if (!v) return "—";

  const d = new Date(v);

  return Number.isNaN(
    d.getTime()
  )
    ? String(v)
    : d.toLocaleDateString("en-GB");
};

const errorMessage = (e) => {
  const status =
    e?.response?.status;

  const data =
    e?.response?.data;

  if (status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to access this report.";
  }

  if (status === 404) {
    return "Report API endpoint was not found.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return data.error;
  }

  if (data?.errors) {
    return Object.entries(
      data.errors
    )
      .map(
        ([k, v]) =>
          `${labelize(k)}: ${formatValue(v)}`
      )
      .join(" | ");
  }

  return (
    e?.message ||
    "Something went wrong while loading the report."
  );
};

const hexToRgb = (hex) => {
  if (!hex) {
    return [0, 0, 0];
  }

  let h = String(hex).replace(
    "#",
    ""
  );

  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if (h.length !== 6) {
    return [0, 0, 0];
  }

  return [
    parseInt(
      h.slice(0, 2),
      16
    ) || 0,

    parseInt(
      h.slice(2, 4),
      16
    ) || 0,

    parseInt(
      h.slice(4, 6),
      16
    ) || 0,
  ];
};

/* =========================================================
   DYNAMIC BRANCH IMAGE HELPERS
========================================================= */

const getAbsoluteImageUrl = (value) => {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (value.startsWith("data:image/")) {
    return value;
  }

  const base = String(BASE_URL).replace(
    /\/api\/?$/,
    ""
  );

  return `${base}${
    value.startsWith("/")
      ? ""
      : "/"
  }${value}`;
};

const blobToDataUrl = async (blob) => {
  return new Promise(
    (resolve, reject) => {
      const reader =
        new FileReader();

      reader.onloadend = () => {
        resolve(
          reader.result
        );
      };

      reader.onerror = reject;

      reader.readAsDataURL(blob);
    }
  );
};

const convertDataUrlToPng = async (
  dataUrl
) => {
  return new Promise(
    (resolve, reject) => {
      const img =
        new Image();

      img.onload = () => {
        const canvas =
          document.createElement(
            "canvas"
          );

        canvas.width =
          img.naturalWidth ||
          img.width;

        canvas.height =
          img.naturalHeight ||
          img.height;

        const ctx =
          canvas.getContext(
            "2d"
          );

        if (!ctx) {
          reject(
            new Error(
              "Unable to create canvas context."
            )
          );
          return;
        }

        ctx.drawImage(
          img,
          0,
          0
        );

        resolve(
          canvas.toDataURL(
            "image/png"
          )
        );
      };

      img.onerror = reject;

      img.src = dataUrl;
    }
  );
};

const imageUrlToDataUrl = async (
  url
) => {
  if (!url) return "";

  if (
    url.startsWith(
      "data:image/"
    )
  ) {
    return convertDataUrlToPng(
      url
    );
  }

  const response =
    await fetch(url);

  if (!response.ok) {
    throw new Error(
      "Unable to load branch image."
    );
  }

  const blob =
    await response.blob();

  const dataUrl =
    await blobToDataUrl(
      blob
    );

  return convertDataUrlToPng(
    dataUrl
  );
};

/* =========================================================
   RESPONSE MAPPING
========================================================= */

const extractReportData = (
  data,
  type
) => {
  if (
    !data ||
    typeof data !== "object"
  ) {
    return {
      rows: [],
      summary: null,
    };
  }

  /* -------------------------------------------------------
     EMPLOYEE REPORT
  ------------------------------------------------------- */

  if (type === "employees") {
    return {
      rows: normalizeArray(
        data?.data
      ),
      summary:
        data?.summary || null,
    };
  }

  /* -------------------------------------------------------
     LEAVE REPORT
  ------------------------------------------------------- */

  if (type === "leaves") {
    return {
      rows: normalizeArray(
        data?.leave_report?.data
      ),
      summary:
        data?.leave_report?.summary ||
        null,
    };
  }

  /* -------------------------------------------------------
     LEAVE BALANCE
  ------------------------------------------------------- */

  if (
    type === "leave-balance"
  ) {
    return {
      rows: normalizeArray(
        data?.leave_balance_report?.data
      ),
      summary:
        data?.leave_balance_report
          ?.summary || null,
    };
  }

  /* -------------------------------------------------------
     DEPARTMENT-WISE LEAVE
  ------------------------------------------------------- */

  if (
    type === "department-leave"
  ) {
    return {
      rows: normalizeArray(
        data
          ?.department_wise_leave_report
          ?.data
      ),
      summary: null,
    };
  }

  /* -------------------------------------------------------
     MONTHLY PAYROLL
  ------------------------------------------------------- */

  if (
    type === "monthly-payroll"
  ) {
    return {
      rows: normalizeArray(
        data
          ?.monthly_payroll_report
          ?.data
      ),
      summary:
        data?.summary || null,
    };
  }

  /* -------------------------------------------------------
     EMPLOYEE PAYROLL
  ------------------------------------------------------- */

  if (
    type === "employee-payroll"
  ) {
    return {
      rows: normalizeArray(
        data
          ?.employee_payroll_report
          ?.data
      ),
      summary:
        data?.summary || null,
    };
  }

  /* -------------------------------------------------------
     DEPARTMENT PAYROLL
  ------------------------------------------------------- */

  if (
    type === "department-payroll"
  ) {
    return {
      rows: normalizeArray(
        data
          ?.department_payroll_report
          ?.data
      ),
      summary:
        data?.summary || null,
    };
  }

  return {
    rows: normalizeArray(
      data?.data ||
        data?.results
    ),
    summary:
      data?.summary ||
      data?.metadata ||
      null,
  };
};

/* =========================================================
   INITIAL FILTERS
========================================================= */

const initialFilters = (
  u = {}
) => ({
  organization:
    u?.organization_id ||
    u?.organization?.id ||
    "",

  employee: "",
  department: "",
  designation: "",
  leave_type: "",
  status: "",
  payment_status: "",
  employment_status: "",

  year:
    new Date().getFullYear(),

  month:
    new Date().getMonth() + 1,

  date_from: "",
  date_to: "",
});

/* =========================================================
   COMPONENT
========================================================= */

export default function HRReports() {
  const user = useMemo(
    () => getUser(),
    []
  );

  /* =======================================================
     GLOBAL BRANCH CONTEXT
  ======================================================= */

  const {
    selectedBranchId,
    branchList,
    organizationId:
      contextOrganizationId,
  } = useBranch();

  const globalBranchId =
    selectedBranchId || "";

  /* =======================================================
     PDF SETTINGS
  ======================================================= */

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  /* =======================================================
     STATES
  ======================================================= */

  const theme = useTheme();

  const isDarkMode =
    theme.palette.mode === "dark";

  const [reportType, setReportType] =
    useState("employees");

  const [filters, setFilters] =
    useState(() =>
      initialFilters(user)
    );

  const [organizations, setOrganizations] =
    useState([]);

  const [employees, setEmployees] =
    useState([]);

  const [leaveTypes, setLeaveTypes] =
    useState([]);

  const [rows, setRows] =
    useState([]);

  const [summary, setSummary] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [filterOpen, setFilterOpen] =
    useState(false);

  const [colorOpen, setColorOpen] =
    useState(false);

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      message: "",
      severity: "success",
    });

  const [tempThemeColor, setTempThemeColor] =
    useState(() =>
      localStorage.getItem("primaryColor") ||
      pdfThemeColor
    );

  const [tempTextColor, setTempTextColor] =
    useState(pdfTextColor);

  /* =======================================================
     BRANCH PDF DETAILS
  ======================================================= */

  const [branchDetails, setBranchDetails] =
    useState(null);

  const [branchLogo, setBranchLogo] =
    useState("");

  const [branchName, setBranchName] =
    useState("");

  /* =======================================================
     BASIC CONFIG
  ======================================================= */

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "";

  const effectiveOrganizationId =
    filters.organization ||
    contextOrganizationId ||
    user?.organization_id ||
    "";

  const config = REPORTS.find(
    (x) => x.key === reportType
  );

  const isLeave = [
    "leaves",
    "leave-balance",
    "department-leave",
  ].includes(reportType);

  const isPayroll = [
    "monthly-payroll",
    "employee-payroll",
    "department-payroll",
  ].includes(reportType);

  const showEmployee = [
    "employees",
    "leaves",
    "leave-balance",
    "employee-payroll",
  ].includes(reportType);

  const showDepartment =
    reportType === "employees" ||
    isLeave ||
    isPayroll;

  const showDesignation = [
    "employees",
    "monthly-payroll",
    "employee-payroll",
    "department-payroll",
  ].includes(reportType);

  const showDates =
    [
      "employees",
      "leaves",
      "department-leave",
    ].includes(reportType) ||
    reportType === "employee-payroll" ||
    reportType ===
      "department-payroll";

  const showYear =
    reportType === "employees" ||
    isLeave ||
    isPayroll;

  const showMonth =
    reportType ===
    "monthly-payroll";

  const showStatus =
    reportType === "leaves" ||
    reportType ===
      "department-leave";

  const showPayment = isPayroll;

  const showEmployment =
    reportType === "employees";

  const showLeaveType =
    isLeave;

  /* =======================================================
     NOTIFICATION
  ======================================================= */

  const notify = useCallback(
    (
      message,
      severity = "success"
    ) => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  /* =======================================================
     ORGANIZATION
  ======================================================= */

  const fetchOrganizations =
    useCallback(
      async () => {
        try {
          const response =
            await API.get(
              "/master/organization/"
            );

          setOrganizations(
            normalizeArray(
              response.data
            )
          );
        } catch (e) {
          console.error(
            "Failed to fetch organizations:",
            e
          );
        }
      },
      []
    );

  /* =======================================================
     EMPLOYEES
     GLOBAL BRANCH USED HERE
  ======================================================= */

  const fetchEmployees =
    useCallback(
      async () => {
        if (
          !effectiveOrganizationId ||
          !globalBranchId
        ) {
          setEmployees([]);
          return;
        }

        try {
          const response =
            await API.get(
              "/staff/employees/filter/",
              {
                params: {
                  organization_id:
                    effectiveOrganizationId,

                  branch_id:
                    globalBranchId,
                },
              }
            );

          setEmployees(
            normalizeArray(
              response.data
            )
          );
        } catch (e) {
          console.error(
            "Failed to fetch employees:",
            e
          );

          setEmployees([]);
        }
      },
      [
        effectiveOrganizationId,
        globalBranchId,
      ]
    );

  /* =======================================================
     LEAVE TYPES
     GLOBAL BRANCH USED HERE
  ======================================================= */

  const fetchLeaveTypes =
    useCallback(
      async () => {
        if (
          !effectiveOrganizationId
        ) {
          setLeaveTypes([]);
          return;
        }

        try {
          const response =
            await API.get(
              "/hr/leave-types/dropdown/",
              {
                params: {
                  organization:
                    effectiveOrganizationId,

                  branch:
                    globalBranchId ||
                    undefined,
                },
              }
            );

          setLeaveTypes(
            normalizeArray(
              response.data
            )
          );
        } catch (e) {
          console.error(
            "Failed to fetch leave types:",
            e
          );

          setLeaveTypes([]);
        }
      },
      [
        effectiveOrganizationId,
        globalBranchId,
      ]
    );

  /* =======================================================
     BRANCH DETAILS
     GLOBAL BRANCH USED HERE
  ======================================================= */

  const fetchBranchDetails =
    useCallback(
      async (branchId) => {
        if (!branchId) {
          setBranchDetails(null);
          setBranchLogo("");
          setBranchName("");
          return;
        }

        try {
          const response =
            await API.get(
              `/master/branch/${branchId}/`
            );

          const data =
            response.data || {};

          const name =
            data.name ||
            data.branch_name ||
            data.branchName ||
            "";

          const image =
            data.image ||
            data.logo ||
            data.branch_logo ||
            data.branchLogo ||
            "";

          setBranchDetails(data);

          setBranchName(name);

          setBranchLogo(
            image
              ? getAbsoluteImageUrl(
                  image
                )
              : ""
          );
        } catch (e) {
          console.error(
            "Failed to fetch branch details:",
            e
          );

          setBranchDetails(null);
          setBranchLogo("");
          setBranchName("");
        }
      },
      []
    );

  /* =======================================================
     INITIAL FETCH
  ======================================================= */

  useEffect(() => {
    fetchOrganizations();
  }, [
    fetchOrganizations,
  ]);

  /* =======================================================
     GLOBAL BRANCH CHANGE
  ======================================================= */

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,

      organization:
        prev.organization ||
        contextOrganizationId ||
        user?.organization_id ||
        "",

      employee: "",
      department: "",
      designation: "",
    }));

    setPage(0);
  }, [
    globalBranchId,
    contextOrganizationId,
    user?.organization_id,
  ]);

  /* =======================================================
     DEPENDENT DATA
  ======================================================= */

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  useEffect(() => {
    fetchBranchDetails(
      globalBranchId
    );
  }, [
    globalBranchId,
    fetchBranchDetails,
  ]);

  /* =======================================================
     PDF COLOR MODAL
  ======================================================= */

  useEffect(() => {
    if (colorOpen) {
      setTempThemeColor(
        pdfThemeColor
      );

      setTempTextColor(
        pdfTextColor
      );
    }
  }, [
    colorOpen,
    pdfThemeColor,
    pdfTextColor,
  ]);

  /* =======================================================
     DEPARTMENTS
  ======================================================= */

  const departments = useMemo(
    () => {
      const map = new Map();

      employees.forEach(
        (employee) => {
          const id =
            employee?.department_id ??
            employee?.department?.id ??
            employee?.department;

          const name =
            employee?.department_name ??
            employee?.department?.name;

          if (
            id != null &&
            name
          ) {
            map.set(
              String(id),
              {
                value: id,
                label: name,
              }
            );
          }
        }
      );

      return [...map.values()];
    },
    [employees]
  );

  /* =======================================================
     DESIGNATIONS
  ======================================================= */

  const designations = useMemo(
    () => {
      const map = new Map();

      employees.forEach(
        (employee) => {
          const id =
            employee?.designation_id ??
            employee?.designation?.id ??
            employee?.designation;

          const name =
            employee?.designation_name ??
            employee?.designation?.name;

          if (
            id != null &&
            name
          ) {
            map.set(
              String(id),
              {
                value: id,
                label: name,
              }
            );
          }
        }
      );

      return [...map.values()];
    },
    [employees]
  );

  /* =======================================================
     FILTER CHANGE
     NO BRANCH FILTER HERE
  ======================================================= */

  const changeFilter = (
    name,
    value
  ) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (
        name ===
        "organization"
      ) {
        next.employee = "";
        next.department = "";
        next.designation = "";
      }

      if (
        name ===
        "department"
      ) {
        next.employee = "";
        next.designation = "";
      }

      return next;
    });

    setPage(0);
  };

  /* =======================================================
     BUILD REPORT PARAMS
     GLOBAL BRANCH USED HERE
  ======================================================= */

  const buildParams =
    useCallback(
      (
        f = filters,
        type = reportType
      ) => {
        const params = {};

        const put = (
          key,
          value
        ) => {
          if (
            value !== "" &&
            value !== null &&
            value !== undefined
          ) {
            params[key] = value;
          }
        };

        put(
          "organization",
          f.organization
        );

        put(
          "branch",
          globalBranchId
        );

        if (
          type === "employees"
        ) {
          put(
            "employee",
            f.employee
          );

          put(
            "department",
            f.department
          );

          put(
            "designation",
            f.designation
          );

          put(
            "employment_status",
            f.employment_status
          );

          put(
            "date_from",
            f.date_from
          );

          put(
            "date_to",
            f.date_to
          );
        }

        if (
          type === "leaves" ||
          type ===
            "department-leave"
        ) {
          if (
            type === "leaves"
          ) {
            put(
              "employee",
              f.employee
            );
          }

          put(
            "department",
            f.department
          );

          put(
            "leave_type",
            f.leave_type
          );

          put(
            "status",
            f.status
          );

          put(
            "year",
            f.year
          );

          put(
            "date_from",
            f.date_from
          );

          put(
            "date_to",
            f.date_to
          );
        }

        if (
          type ===
          "leave-balance"
        ) {
          put(
            "employee",
            f.employee
          );

          put(
            "department",
            f.department
          );

          put(
            "leave_type",
            f.leave_type
          );

          put(
            "year",
            f.year
          );
        }

        if (
          type ===
          "monthly-payroll"
        ) {
          put(
            "department",
            f.department
          );

          put(
            "designation",
            f.designation
          );

          put(
            "month",
            f.month
          );

          put(
            "year",
            f.year
          );

          put(
            "payment_status",
            f.payment_status
          );
        }

        if (
          type ===
          "employee-payroll"
        ) {
          put(
            "employee",
            f.employee
          );

          put(
            "department",
            f.department
          );

          put(
            "designation",
            f.designation
          );

          put(
            "month",
            f.month
          );

          put(
            "year",
            f.year
          );

          put(
            "payment_status",
            f.payment_status
          );

          put(
            "date_from",
            f.date_from
          );

          put(
            "date_to",
            f.date_to
          );
        }

        if (
          type ===
          "department-payroll"
        ) {
          put(
            "department",
            f.department
          );

          put(
            "designation",
            f.designation
          );

          put(
            "month",
            f.month
          );

          put(
            "year",
            f.year
          );

          put(
            "payment_status",
            f.payment_status
          );

          put(
            "date_from",
            f.date_from
          );

          put(
            "date_to",
            f.date_to
          );
        }

        return params;
      },
      [
        filters,
        reportType,
        globalBranchId,
      ]
    );

  /* =======================================================
     FETCH REPORT
     ONLY RESPONSE MAPPING FIXED
  ======================================================= */

  const fetchReport =
    useCallback(
      async (
        f = filters,
        type = reportType
      ) => {
        if (!f.organization) {
          return [];
        }

        if (!globalBranchId) {
          setRows([]);
          setSummary(null);
          setError(
            "Please select a branch from the global Branch selector."
          );
          return [];
        }

        try {
          setLoading(true);
          setError("");

          const reportConfig =
            REPORTS.find(
              (item) =>
                item.key === type
            );

          if (!reportConfig) {
            setRows([]);
            setSummary(null);
            setError(
              "Invalid report type selected."
            );
            return [];
          }

          const params =
            buildParams(
              f,
              type
            );

          const response =
            await API.get(
              reportConfig.api,
              {
                params,
              }
            );

          const reportData =
            extractReportData(
              response.data,
              type
            );

          setRows(
            reportData.rows
          );

          setSummary(
            reportData.summary
          );

          setPage(0);

          if (
            !reportData.rows.length
          ) {
            setError(
              "No report data found for the selected filters."
            );
          }

          return reportData.rows;
        } catch (e) {
          const message =
            errorMessage(e);

          setRows([]);
          setSummary(null);
          setError(message);

          notify(
            message,
            "error"
          );
          return [];
        } finally {
          setLoading(false);
        }
      },
      [
        filters,
        reportType,
        globalBranchId,
        buildParams,
        notify,
      ]
    );

  /* =======================================================
     AUTO FETCH REPORT
  ======================================================= */

  useEffect(() => {
    if (
      filters.organization &&
      globalBranchId
    ) {
      fetchReport(
        filters,
        reportType
      );
    }
  }, [
    reportType,
    globalBranchId,
    filters.organization,
    fetchReport,
  ]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredRows =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      if (!query) {
        return rows;
      }

      return rows.filter(
        (row) =>
          Object.values(
            row || {}
          ).some((value) =>
            formatValue(value)
              .toLowerCase()
              .includes(query)
          )
      );
    }, [
      rows,
      search,
    ]);

  /* =======================================================
     COLUMNS
  ======================================================= */

  const columns = useMemo(
    () => {
      const preferred =
        REPORT_COLUMNS[
          reportType
        ] || [];

      if (!rows.length) {
        return preferred;
      }

      const keys =
        new Set();

      rows.forEach(
        (row) => {
          Object.keys(
            row || {}
          ).forEach(
            (key) =>
              keys.add(key)
          );
        }
      );

      return [
        ...preferred.filter(
          (key) =>
            keys.has(key)
        ),

        ...Array.from(
          keys
        ).filter(
          (key) =>
            !preferred.includes(
              key
            )
        ),
      ];
    },
    [
      rows,
      reportType,
    ]
  );

  /* =======================================================
     PAGINATION
  ======================================================= */

  const paginated =
    useMemo(
      () =>
        filteredRows.slice(
          page *
            rowsPerPage,

          page *
              rowsPerPage +
            rowsPerPage
        ),
      [
        filteredRows,
        page,
        rowsPerPage,
      ]
    );

  /* =======================================================
     RESET
  ======================================================= */

  const reset = () => {
    const nextFilters =
      initialFilters(user);

    setFilters(
      nextFilters
    );

    setSearch("");
    setPage(0);
    setError("");

    fetchReport(
      nextFilters,
      reportType
    );
  };

  const handleGenerateReport = async () => {
    const refreshedRows = await fetchReport(filters, reportType);

    if (refreshedRows.length) {
      const globalSidebarColor =
        localStorage.getItem("primaryColor") ||
        pdfThemeColor;

      setTempThemeColor(globalSidebarColor);
      setTempTextColor(pdfTextColor);
      setColorOpen(true);
    }
  };

  const exportExcel = () => {
    if (!rows.length) {
      setError("No HR report data available to export.");
      return;
    }

    try {
      const sheetRows = rows.map((row, index) => ({
        "S.No": index + 1,
        ...Object.entries(row || {}).reduce((acc, [key, value]) => {
          acc[key] = value ?? "-";
          return acc;
        }, {}),
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      worksheet["!cols"] = Object.keys(sheetRows[0] || {}).map((key) => ({
        wch: Math.max(12, String(key).length + 4),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        (REPORTS.find((item) => item.key === reportType)?.label || "HR Report").replace(/\s+/g, "_")
      );
      XLSX.writeFile(
        workbook,
        `${(REPORTS.find((item) => item.key === reportType)?.label || "HR_Report").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      console.error("HR report Excel error:", error);
      setError("Failed to export Excel report.");
    }
  };

  /* =======================================================
     DISPLAY
  ======================================================= */

  const display = (
    row,
    column
  ) => {
    if (
      column.includes(
        "date"
      ) ||
      column.endsWith(
        "_from"
      ) ||
      column.endsWith(
        "_to"
      )
    ) {
      return formatDate(
        row?.[column]
      );
    }

    return formatValue(
      row?.[column]
    );
  };

  /* =======================================================
     PDF GENERATION
     ONLY PDF TABLE COLUMNS + FIT FIX
  ======================================================= */

  const generatePDF = async (
    theme =
      localStorage.getItem("primaryColor") ||
      tempThemeColor ||
      pdfThemeColor,
    text = tempTextColor
  ) => {
    try {
      setPdfLoading(true);

      if (!globalBranchId) {
        notify(
          "Please select a branch from the global Branch selector.",
          "warning"
        );
        return;
      }

      const reportConfig =
        REPORTS.find(
          (item) =>
            item.key === reportType
        );

      if (!reportConfig) {
        notify(
          "Invalid report type selected.",
          "error"
        );
        return;
      }

      const params =
        buildParams(
          filters,
          reportType,
          globalBranchId
        );

      const response =
        await API.get(
          reportConfig.api,
          {
            params,
          }
        );

      /* ==========================================
         SAME RESPONSE MAPPING AS TABLE
      ========================================== */

      const reportData =
        extractReportData(
          response.data,
          reportType
        );

      const data =
        reportData.rows;

      if (!data.length) {
        notify(
          "No report data available for PDF.",
          "warning"
        );
        return;
      }

      /* ---------------------------------------------------
         FETCH CURRENT GLOBAL BRANCH DETAILS
      --------------------------------------------------- */

      let currentBranchName =
        branchName;

      let currentBranchLogo =
        branchLogo;

      try {
        const branchResponse =
          await API.get(
            `/master/branch/${globalBranchId}/`
          );

        const branchData =
          branchResponse.data ||
          {};

        currentBranchName =
          branchData.name ||
          branchData.branch_name ||
          branchData.branchName ||
          currentBranchName ||
          "";

        const image =
          branchData.image ||
          branchData.logo ||
          branchData.branch_logo ||
          branchData.branchLogo ||
          "";

        if (image) {
          const absoluteUrl =
            getAbsoluteImageUrl(
              image
            );

          currentBranchLogo =
            await imageUrlToDataUrl(
              absoluteUrl
            );
        }
      } catch (branchError) {
        console.error(
          "Failed to load branch PDF details:",
          branchError
        );
      }

      /* ---------------------------------------------------
         PDF
      --------------------------------------------------- */

      const doc =
        new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });

      const width =
        doc.internal.pageSize.getWidth();

      const height =
        doc.internal.pageSize.getHeight();

      const margin = 10;

      const themeRgb =
        hexToRgb(theme || pdfThemeColor);

      const textRgb =
        hexToRgb(text);

      /* ---------------------------------------------------
         HEADER BACKGROUND
      --------------------------------------------------- */

      doc.setFillColor(
        ...themeRgb
      );

      doc.rect(
        0,
        0,
        width,
        34,
        "F"
      );

      /* ---------------------------------------------------
         BRANCH LOGO
      --------------------------------------------------- */

      const logoWidth = 18;
      const logoHeight = 18;

      const logoX = margin;
      const logoY = 8;

      if (currentBranchLogo) {
        try {
          doc.addImage(
            currentBranchLogo,
            "PNG",
            logoX,
            logoY,
            logoWidth,
            logoHeight
          );
        } catch (logoError) {
          console.error(
            "Unable to add branch logo:",
            logoError
          );
        }
      }

      /* ---------------------------------------------------
         HEADER TEXT
      --------------------------------------------------- */

      const textCenterY = 17;

      doc.setTextColor(
        255,
        255,
        255
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(15);

      doc.text(
        organizationName ||
          "Organization",
        width / 2,
        textCenterY - 6,
        {
          align: "center",
        }
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10.5);

      doc.text(
        currentBranchName ||
          "Branch",
        width / 2,
        textCenterY,
        {
          align: "center",
        }
      );

      doc.setFontSize(9.5);

      doc.text(
        reportConfig.label,
        width / 2,
        textCenterY + 6,
        {
          align: "center",
        }
      );

      /* ---------------------------------------------------
         GENERATED DATE
      --------------------------------------------------- */

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(8);

      doc.text(
        `Generated: ${new Date().toLocaleString()}`,
        width - margin,
        textCenterY,
        {
          align: "right",
        }
      );

      /* ---------------------------------------------------
         FILTERS
      --------------------------------------------------- */

      let y = 42;

      doc.setTextColor(
        ...textRgb
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(9);

      doc.text(
        "FILTERS",
        margin,
        y
      );

      y += 5;

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(7.5);

      const filterText =
        Object.entries(
          params
        )
          .map(
            ([key, value]) =>
              `${labelize(key)}: ${value}`
          )
          .join(
            "   |   "
          );

      const filterLines =
        doc.splitTextToSize(
          filterText ||
            "All filters",
          width -
            margin * 2
        );

      doc.text(
        filterLines,
        margin,
        y
      );

      y +=
        filterLines.length *
          4 +
        5;

      /* ---------------------------------------------------
         SUMMARY
      --------------------------------------------------- */

      const pdfSummary =
        reportData.summary;

      if (
        pdfSummary &&
        typeof pdfSummary ===
          "object"
      ) {
        const summaryEntries =
          Object.entries(
            pdfSummary
          ).filter(
            ([, value]) =>
              value != null &&
              typeof value !==
                "object"
          );

        if (
          summaryEntries.length
        ) {
          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.text(
            "SUMMARY",
            margin,
            y
          );

          y += 5;

          doc.setFont(
            "helvetica",
            "normal"
          );

          const summaryText =
            summaryEntries
              .map(
                ([key, value]) =>
                  `${labelize(key)}: ${value}`
              )
              .join(
                "   |   "
              );

          const summaryLines =
            doc.splitTextToSize(
              summaryText,
              width -
                margin * 2
            );

          doc.text(
            summaryLines,
            margin,
            y
          );

          y +=
            summaryLines.length *
              4 +
            5;
        }
      }

      /* ---------------------------------------------------
         PDF TABLE COLUMNS
         ONLY NECESSARY COLUMNS
      --------------------------------------------------- */

      const keySet =
        new Set();

      data.forEach(
        (row) => {
          Object.keys(
            row || {}
          ).forEach(
            (key) =>
              keySet.add(key)
          );
        }
      );

      const preferredPdfColumns =
        REPORT_PDF_COLUMNS[
          reportType
        ] || [];

      const pdfColumns =
        preferredPdfColumns.filter(
          (key) =>
            keySet.has(key)
        );

      /* ---------------------------------------------------
         PAGE WIDTH SETTINGS
      --------------------------------------------------- */

      const availableTableWidth =
        width -
        margin * 2;

      const pdfColumnStyles = {};

      pdfColumns.forEach(
        (column) => {
          pdfColumnStyles[
            column
          ] = {
            cellWidth:
              "wrap",
          };
        }
      );

      /* ---------------------------------------------------
         SPECIAL WIDTHS
         Keeps important fields readable while still
         fitting inside A4 landscape.
      --------------------------------------------------- */

      if (
        reportType ===
        "employees"
      ) {
        pdfColumnStyles.employee_code = {
          cellWidth: 21,
        };

        pdfColumnStyles.name = {
          cellWidth: 28,
        };

        pdfColumnStyles.department_name = {
          cellWidth: 30,
        };

        pdfColumnStyles.designation_name = {
          cellWidth: 27,
        };

        pdfColumnStyles.date_of_joining = {
          cellWidth: 25,
        };

        pdfColumnStyles.mobile = {
          cellWidth: 23,
        };

        pdfColumnStyles.email = {
          cellWidth: 38,
        };

        pdfColumnStyles.employment_status = {
          cellWidth: 28,
        };
      }

      if (
        reportType ===
        "leaves"
      ) {
        pdfColumnStyles.employee_code = {
          cellWidth: 21,
        };

        pdfColumnStyles.employee_name = {
          cellWidth: 28,
        };

        pdfColumnStyles.leave_type_name = {
          cellWidth: 29,
        };

        pdfColumnStyles.from_date = {
          cellWidth: 24,
        };

        pdfColumnStyles.to_date = {
          cellWidth: 24,
        };

        pdfColumnStyles.total_days = {
          cellWidth: 18,
        };

        pdfColumnStyles.status = {
          cellWidth: 22,
        };

        pdfColumnStyles.reason = {
          cellWidth: 45,
        };
      }

      if (
        reportType ===
        "leave-balance"
      ) {
        pdfColumnStyles.employee_code = {
          cellWidth: 21,
        };

        pdfColumnStyles.employee_name = {
          cellWidth: 34,
        };

        pdfColumnStyles.leave_type_name = {
          cellWidth: 34,
        };

        pdfColumnStyles.allocated_days = {
          cellWidth: 24,
        };

        pdfColumnStyles.used_days = {
          cellWidth: 22,
        };

        pdfColumnStyles.remaining_days = {
          cellWidth: 27,
        };

        pdfColumnStyles.year = {
          cellWidth: 18,
        };
      }

      if (
        reportType ===
        "department-leave"
      ) {
        pdfColumnStyles.department_name = {
          cellWidth: 40,
        };

        pdfColumnStyles.total_applications = {
          cellWidth: 29,
        };

        pdfColumnStyles.pending_applications = {
          cellWidth: 29,
        };

        pdfColumnStyles.approved_applications = {
          cellWidth: 29,
        };

        pdfColumnStyles.rejected_applications = {
          cellWidth: 29,
        };

        pdfColumnStyles.total_days = {
          cellWidth: 24,
        };

        pdfColumnStyles.approved_days = {
          cellWidth: 24,
        };
      }

      if (
        reportType ===
        "monthly-payroll"
      ) {
        pdfColumnStyles.year = {
          cellWidth: 17,
        };

        pdfColumnStyles.month = {
          cellWidth: 17,
        };

        pdfColumnStyles.employee_count = {
          cellWidth: 24,
        };

        pdfColumnStyles.total_gross_salary = {
          cellWidth: 31,
        };

        pdfColumnStyles.total_deduction = {
          cellWidth: 28,
        };

        pdfColumnStyles.total_net_salary = {
          cellWidth: 30,
        };

        pdfColumnStyles.total_working_days = {
          cellWidth: 29,
        };

        pdfColumnStyles.total_leave_days = {
          cellWidth: 25,
        };
      }

      if (
        reportType ===
        "employee-payroll"
      ) {
        pdfColumnStyles.employee_code = {
          cellWidth: 21,
        };

        pdfColumnStyles.employee_name = {
          cellWidth: 30,
        };

        pdfColumnStyles.department_name = {
          cellWidth: 28,
        };

        pdfColumnStyles.designation_name = {
          cellWidth: 27,
        };

        pdfColumnStyles.total_gross_salary = {
          cellWidth: 30,
        };

        pdfColumnStyles.total_deduction = {
          cellWidth: 27,
        };

        pdfColumnStyles.total_net_salary = {
          cellWidth: 30,
        };

        pdfColumnStyles.total_working_days = {
          cellWidth: 28,
        };

        pdfColumnStyles.total_leave_days = {
          cellWidth: 25,
        };
      }

      if (
        reportType ===
        "department-payroll"
      ) {
        pdfColumnStyles.department_name = {
          cellWidth: 36,
        };

        pdfColumnStyles.employee_count = {
          cellWidth: 23,
        };

        pdfColumnStyles.payroll_count = {
          cellWidth: 22,
        };

        pdfColumnStyles.total_gross_salary = {
          cellWidth: 30,
        };

        pdfColumnStyles.total_deduction = {
          cellWidth: 28,
        };

        pdfColumnStyles.total_net_salary = {
          cellWidth: 30,
        };

        pdfColumnStyles.total_working_days = {
          cellWidth: 28,
        };

        pdfColumnStyles.total_leave_days = {
          cellWidth: 25,
        };
      }

      /* ---------------------------------------------------
         TABLE
         AUTO FIT TO A4 LANDSCAPE PAGE
      --------------------------------------------------- */

      autoTable(
        doc,
        {
          startY: y,

          head: [
            pdfColumns.map(
              (column) =>
                labelize(
                  column
                )
            ),
          ],

          body: data.map(
            (row) =>
              pdfColumns.map(
                (column) => {
                  if (
                    column.includes(
                      "date"
                    ) ||
                    column.endsWith(
                      "_from"
                    ) ||
                    column.endsWith(
                      "_to"
                    )
                  ) {
                    return formatDate(
                      row?.[
                        column
                      ]
                    );
                  }

                  return formatValue(
                    row?.[
                      column
                    ]
                  );
                }
              )
          ),

          theme: "grid",

          tableWidth:
            availableTableWidth,

          margin: {
            left: margin,
            right: margin,
            bottom: 12,
          },

          styles: {
            font: "helvetica",

            fontSize: 6.5,

            cellPadding: 1.8,

            lineColor: themeRgb,

            lineWidth: 0.1,

            textColor:
              textRgb,

            valign:
              "middle",

            halign:
              "center",

            overflow:
              "linebreak",

            cellWidth:
              "wrap",

            minCellHeight: 7,
          },

          headStyles: {
            fillColor:
              themeRgb,

            textColor: [
              255,
              255,
              255,
            ],

            fontStyle:
              "bold",

            fontSize: 6.8,

            halign:
              "center",

            valign:
              "middle",

            overflow:
              "linebreak",
          },

          bodyStyles: {
            fontSize: 6.5,
          },

          alternateRowStyles: {
            fillColor: [
              248,
              250,
              252,
            ],
          },

          columnStyles:
            pdfColumnStyles,

          didDrawPage: () => {
            const currentPage =
              doc.internal.getNumberOfPages();

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(7);

            doc.setTextColor(
              ...textRgb
            );

            doc.text(
              "HR Reports",
              margin,
              height - 5
            );

            doc.text(
              `Page ${currentPage}`,
              width - margin,
              height - 5,
              {
                align: "right",
              }
            );
          },
        }
      );

      /* ---------------------------------------------------
         OPEN PDF
      --------------------------------------------------- */

      const blob =
        doc.output("blob");

      const url =
        URL.createObjectURL(
          blob
        );

      const win =
        window.open(
          url,
          "_blank"
        );

      if (!win) {
        const anchor =
          document.createElement(
            "a"
          );

        anchor.href = url;

        anchor.download =
          `${reportType.replace(
            /-/g,
            "_"
          )}_report_${new Date()
            .toISOString()
            .slice(
              0,
              10
            )}.pdf`;

        anchor.click();
      }

      setTimeout(
        () =>
          URL.revokeObjectURL(
            url
          ),
        10000
      );

      notify(
        "PDF generated successfully."
      );
    } catch (e) {
      console.error(
        "PDF generation error:",
        e
      );

      notify(
        errorMessage(e),
        "error"
      );
    } finally {
      setPdfLoading(false);
    }
  };

  /* =======================================================
     SELECT FIELD
  ======================================================= */

  const selectField = ({
    label,
    name,
    value,
    options = [],
    disabled = false,
  }) => (
    <FormControl
      fullWidth
      size="small"
      disabled={disabled}
    >
      <InputLabel
        sx={{
          color: "text.disabled",

          "&.Mui-focused": {
            color: "primary.main",
          },
        }}
      >
        {label}
      </InputLabel>

      <Select
        value={value ?? ""}
        label={label}
        onChange={(event) =>
          changeFilter(
            name,
            event.target.value
          )
        }
      >
        <MenuItem value="">
          All {label}s
        </MenuItem>

        {options.map(
          (option, index) => (
            <MenuItem
              key={`${option.value}-${index}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );

  /* =======================================================
     DATE FIELD
  ======================================================= */

  const dateField = (
    label,
    name
  ) => (
    <TextField
      fullWidth
      size="small"
      type="date"
      label={label}
      InputLabelProps={{
        shrink: true,
      }}
      value={
        filters[name] || ""
      }
      onChange={(event) =>
        changeFilter(
          name,
          event.target.value
        )
      }
    />
  );

  /* =======================================================
     OPTIONS
  ======================================================= */

  const employeeOptions =
    employees.map(
      (employee) => ({
        value: getId(
          employee
        ),
        label:
          employee?.name ||
          employee?.full_name ||
          employee?.employee_name ||
          employee?.employee_code ||
          `Employee ${getId(
            employee
          )}`,
      })
    );

  const orgOptions =
    organizations.map(
      (organization) => ({
        value: getId(
          organization
        ),
        label: getName(
          organization,
          `Organization ${getId(
            organization
          )}`
        ),
      })
    );

  const leaveOptions =
    leaveTypes.map(
      (leave) => ({
        value: getId(
          leave
        ),
        label:
          leave?.name ||
          leave?.leave_type_name ||
          leave?.label ||
          `Leave Type ${getId(
            leave
          )}`,
      })
    );

  /* =======================================================
     FILTER MODAL
     GLOBAL BRANCH - NO BRANCH DROPDOWN
  ======================================================= */

  const filterModal = (
    <Modal
      open={filterOpen}
      onClose={() =>
        setFilterOpen(false)
      }
      aria-labelledby="hr-report-filters"
      BackdropProps={{
        sx: {
          backgroundColor:
            "rgba(15, 23, 42, 0.62)",
          backdropFilter:
            "blur(4px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",

          width: {
            xs: "calc(100% - 24px)",
            sm: 680,
            md: 780,
          },

          maxWidth: 780,
          maxHeight: "92vh",

          display: "flex",
          flexDirection: "column",

          bgcolor:
            "background.paper",
          color: "text.primary",

          border: "1px solid",
          borderColor: "divider",

          borderRadius: 3,

          boxShadow:
            "0 24px 70px rgba(0, 0, 0, 0.22)",

          outline: "none",

          overflow: "hidden",
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",

            borderBottom:
              "1px solid",

            borderColor:
              "divider",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",

            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 16,
                  sm: 18,
                },

                fontWeight: 700,

                color:
                  "text.primary",

                lineHeight: 1.3,
              }}
            >
              HR Report Filters
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.4,

                color:
                  "text.disabled",

                fontSize: 12.5,

                whiteSpace:
                  "nowrap",

                overflow:
                  "hidden",

                textOverflow:
                  "ellipsis",
              }}
            >
              {config?.label ||
                "Configure report filters"}
            </Typography>
          </Box>

          <IconButton
            onClick={() =>
              setFilterOpen(false)
            }
            size="small"
            aria-label="Close filters"
            sx={{
              ml: 2,

              width: 34,
              height: 34,

              border:
                "1px solid",

              borderColor:
                "divider",

              borderRadius: 2,

              color:
                "text.disabled",

              "&:hover": {
                bgcolor:
                  "action.hover",

                color:
                  "text.primary",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* CONTENT */}

        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },

            overflowY: "auto",

            flex: 1,

            "&::-webkit-scrollbar": {
              width: 7,
            },

            "&::-webkit-scrollbar-track": {
              background:
                "transparent",
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                "rgba(128,128,128,0.35)",
              borderRadius: 10,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* ORGANIZATION */}

            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm:
                    "calc(50% - 8px)",
                },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Organization"
                value={
                  organizationName ||
                  (filters.organization
                    ? `Organization ${filters.organization}`
                    : "No organization selected")
                }
                disabled
              />
            </Box>

            {/* GLOBAL BRANCH */}

            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm:
                    "calc(50% - 8px)",
                },
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Global Branch"
                value={
                  branchName ||
                  branchList?.find(
                    (branch) =>
                      String(
                        getId(branch)
                      ) ===
                      String(
                        globalBranchId
                      )
                  )?.name ||
                  branchList?.find(
                    (branch) =>
                      String(
                        getId(branch)
                      ) ===
                      String(
                        globalBranchId
                      )
                  )?.branch_name ||
                  (globalBranchId
                    ? `Branch ${globalBranchId}`
                    : "No branch selected")
                }
                disabled
              />
            </Box>

            {/* EMPLOYEE */}

            {showEmployee && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label: "Employee",
                  name: "employee",
                  value:
                    filters.employee,
                  options:
                    employeeOptions,
                  disabled:
                    !employees.length,
                })}
              </Box>
            )}

            {/* DEPARTMENT */}

            {showDepartment && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label:
                    "Department",
                  name:
                    "department",
                  value:
                    filters.department,
                  options:
                    departments,
                  disabled:
                    !departments.length,
                })}
              </Box>
            )}

            {/* DESIGNATION */}

            {showDesignation && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label:
                    "Designation",
                  name:
                    "designation",
                  value:
                    filters.designation,
                  options:
                    designations,
                  disabled:
                    !designations.length,
                })}
              </Box>
            )}

            {/* LEAVE TYPE */}

            {showLeaveType && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label:
                    "Leave Type",
                  name:
                    "leave_type",
                  value:
                    filters.leave_type,
                  options:
                    leaveOptions,
                  disabled:
                    !leaveOptions.length,
                })}
              </Box>
            )}

            {/* STATUS */}

            {showStatus && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label: "Status",
                  name: "status",
                  value:
                    filters.status,
                  options: [
                    "pending",
                    "approved",
                    "rejected",
                    "cancelled",
                  ].map(
                    (value) => ({
                      value,
                      label:
                        labelize(
                          value
                        ),
                    })
                  ),
                })}
              </Box>
            )}

            {/* EMPLOYMENT STATUS */}

            {showEmployment && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label:
                    "Employment Status",
                  name:
                    "employment_status",
                  value:
                    filters.employment_status,
                  options: [
                    "active",
                    "inactive",
                    "terminated",
                    "resigned",
                  ].map(
                    (value) => ({
                      value,
                      label:
                        labelize(
                          value
                        ),
                    })
                  ),
                })}
              </Box>
            )}

            {/* PAYMENT STATUS */}

            {showPayment && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label:
                    "Payment Status",
                  name:
                    "payment_status",
                  value:
                    filters.payment_status,
                  options: [
                    "paid",
                    "pending",
                    "failed",
                  ].map(
                    (value) => ({
                      value,
                      label:
                        labelize(
                          value
                        ),
                    })
                  ),
                })}
              </Box>
            )}

            {/* YEAR */}

            {showYear && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Year"
                  value={
                    filters.year
                  }
                  onChange={(
                    event
                  ) =>
                    changeFilter(
                      "year",
                      event.target
                        .value
                    )
                  }
                  inputProps={{
                    min: 2000,
                    max: 2100,
                  }}
                  sx={{
                    "& .MuiInputLabel-root":
                      {
                        color:
                          "text.primary",
                      },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          "primary.main",
                      },
                  }}
                />
              </Box>
            )}

            {/* MONTH */}

            {showMonth && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                {selectField({
                  label: "Month",
                  name: "month",
                  value:
                    filters.month,
                  options: [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map(
                    (
                      value,
                      index
                    ) => ({
                      value:
                        index + 1,
                      label:
                        value,
                    })
                  ),
                })}
              </Box>
            )}

            {/* DATE FROM */}

            {showDates && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date From"
                  value={
                    filters.date_from
                  }
                  onChange={(
                    event
                  ) =>
                    changeFilter(
                      "date_from",
                      event.target
                        .value
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiInputLabel-root":
                      {
                        color:
                          "text.primary",
                      },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          "primary.main",
                      },

                    "& .MuiInputBase-input":
                      {
                        color:
                          "text.primary",

                        WebkitTextFillColor:
                          "currentColor",
                      },

                    "& input[type='date']::-webkit-calendar-picker-indicator":
                      {
                        opacity: 1,
                        cursor:
                          "pointer",

                        filter:
                          isDarkMode
                            ? "invert(1)"
                            : "none",
                      },

                    "& input[type='date']::-webkit-datetime-edit":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-fields-wrapper":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-text":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-month-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-day-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-year-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-month-field:focus, & input[type='date']::-webkit-datetime-edit-day-field:focus, & input[type='date']::-webkit-datetime-edit-year-field:focus":
                      {
                        backgroundColor:
                          "primary.main",

                        color:
                          "primary.contrastText",
                      },
                  }}
                />
              </Box>
            )}

            {/* DATE TO */}

            {showDates && (
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm:
                      "calc(50% - 8px)",
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date To"
                  value={
                    filters.date_to
                  }
                  onChange={(
                    event
                  ) =>
                    changeFilter(
                      "date_to",
                      event.target
                        .value
                    )
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiInputLabel-root":
                      {
                        color:
                          "text.primary",
                      },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          "primary.main",
                      },

                    "& .MuiInputBase-input":
                      {
                        color:
                          "text.primary",

                        WebkitTextFillColor:
                          "currentColor",
                      },

                    "& input[type='date']::-webkit-calendar-picker-indicator":
                      {
                        opacity: 1,
                        cursor:
                          "pointer",

                        filter:
                          isDarkMode
                            ? "invert(1)"
                            : "none",
                      },

                    "& input[type='date']::-webkit-datetime-edit":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-fields-wrapper":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-text":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-month-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-day-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-year-field":
                      {
                        color:
                          "text.primary",
                      },

                    "& input[type='date']::-webkit-datetime-edit-month-field:focus, & input[type='date']::-webkit-datetime-edit-day-field:focus, & input[type='date']::-webkit-datetime-edit-year-field:focus":
                      {
                        backgroundColor:
                          "primary.main",

                        color:
                          "primary.contrastText",
                      },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* FOOTER */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 1.7,

            display: "flex",

            alignItems: {
              xs: "stretch",
              sm: "center",
            },

            justifyContent:
              "space-between",

            flexDirection: {
              xs: "column",
              sm: "row",
            },
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            backgroundColor: "background.paper",

            gap: 1.5,

            borderTop:
              "1px solid",

            borderColor:
              "divider",

            flexShrink: 0,
          }}
        >
          <Button
            variant="text"
            startIcon={
              <RestartAlt />
            }
            onClick={reset}
            sx={{
              textTransform:
                "none",

              fontWeight: 600,

              color:
                "text.disabled",

              alignSelf: {
                xs: "flex-start",
                sm: "auto",
              },

              "&:hover": {
                bgcolor:
                  "action.hover",

                color:
                  "text.primary",
              },
            }}
          >
            Reset
          </Button>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            width={{
              xs: "100%",
              sm: "auto",
            }}
          >
            <Button
              variant="outlined"
              onClick={() =>
                setFilterOpen(
                  false
                )
              }
              sx={{
                textTransform:
                  "none",

                borderRadius: 2,

                minWidth: 90,

                borderColor:
                  "divider",

                color:
                  "text.primary",

                "&:hover": {
                  borderColor:
                    "text.disabled",

                  bgcolor:
                    "action.hover",
                },
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={
                <FilterAltOutlined />
              }
              disabled={
                loading ||
                !globalBranchId
              }
              onClick={() => {
                fetchReport(
                  filters,
                  reportType
                );

                setFilterOpen(
                  false
                );
              }}
              sx={{
                textTransform:
                  "none",

                borderRadius: 2,

                minWidth: 135,

                fontWeight: 600,

                boxShadow: "none",

                "&:hover": {
                  boxShadow:
                    "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              {loading
                ? "Applying..."
                : "Apply Filters"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
        }}
      >
        {/* REPORT TABS */}

{/* REPORT TABS */}
<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={reportType}
    onChange={(_, value) => {
      setReportType(value);
      setRows([]);
      setSummary(null);
      setError("");
      setSearch("");
      setPage(0);
    }}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-flexContainer": {
        gap: 0.5,
      },

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",

        px: {
          xs: 1.5,
          sm: 2.2,
        },

        borderRadius: 1.5,

        color: "text.primary",

        fontWeight: 600,

        textTransform: "none",

        fontSize: {
          xs: 13,
          sm: 14,
        },

        opacity: 1,

        whiteSpace: "nowrap",

        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },

      "& .MuiTabs-scrollButtons": {
        color: "text.primary",

        "&.Mui-disabled": {
          opacity: 0.3,
        },
      },
    }}
  >
    {REPORTS.map((report) => (
      <Tab
        key={report.key}
        value={report.key}
        label={report.label}
      />
    ))}
  </Tabs>
</Box>


        {/* TOOLBAR */}

        <Box
          sx={{
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "space-between",
            gap: 2,
            mt: 2,
            mb: 2,
            flexWrap:
              "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems:
                "center",
              gap: 1,
              flexWrap:
                "wrap",

              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            {globalBranchId && (
              <Chip
                label={
                  branchName ||
                  branchList?.find(
                    (branch) =>
                      String(
                        getId(
                          branch
                        )
                      ) ===
                      String(
                        globalBranchId
                      )
                  )?.name ||
                  branchList?.find(
                    (branch) =>
                      String(
                        getId(
                          branch
                        )
                      ) ===
                      String(
                        globalBranchId
                      )
                  )?.branch_name ||
                  "Branch selected"
                }
                size="small"
                variant="outlined"
              />
            )}

            {filters.status && (
              <Chip
                label={labelize(
                  filters.status
                )}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems:
                "center",
              gap: 1,
              flexWrap:
                "wrap",

              width: {
                xs: "100%",
                md: "auto",
              },

              justifyContent: {
                xs: "flex-start",
                md: "flex-end",
              },
            }}
          >
            <Button
              variant="contained"
              startIcon={
                <FilterAltOutlined />
              }
              onClick={() =>
                setFilterOpen(
                  true
                )
              }
              sx={{
                textTransform:
                  "none",
                borderRadius: 2,
                px: 2,
                fontWeight: 600,
              }}
            >
              Filters
            </Button>

            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AssessmentOutlined />
                )
              }
              onClick={handleGenerateReport}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                fontWeight: 600,
              }}
            >
              {loading ? "Generating..." : "Generate Report"}
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadOutlined />}
              onClick={exportExcel}
              disabled={loading || !rows.length}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                fontWeight: 600,
              }}
            >
              Export Excel
            </Button>

          </Box>
        </Box>

        {/* SUMMARY */}

        {summary &&
          typeof summary ===
            "object" && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.5,
                backgroundColor:
                  "transparent",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
              >
                {Object.entries(
                  summary
                )
                  .filter(
                    ([, value]) =>
                      typeof value !==
                      "object"
                  )
                  .map(
                    ([
                      key,
                      value,
                    ]) => (
                      <Chip
                        key={key}
                        size="small"
                        variant="outlined"
                        label={`${labelize(
                          key
                        )}: ${formatValue(
                          value
                        )}`}
                      />
                    )
                  )}
              </Stack>
            </Paper>
          )}

        {/* TABLE / STATES */}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent:
                "center",
              py: 9,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        ) : !filteredRows.length ? (
          <Box
            sx={{
              py: 9,
              textAlign:
                "center",
            }}
          >
            <AssessmentOutlined
              sx={{
                fontSize: 42,
                color:
                  "text.disabled",
                mb: 1,
              }}
            />

            <Typography
              fontWeight={600}
            >
              No report data found
            </Typography>

            <Typography
              variant="body2"
              color="text.disabled"
            >
              Try changing the
              filters or
              refreshing the
              report.
            </Typography>
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 2,
              border: 1,
              borderColor:
                "divider",
              overflow: "hidden",
              backgroundColor:
                "background.paper",
            }}
          >
            <TableContainer
              sx={{
                overflowX:
                  "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: 1000,
                }}
              >
                <TableHead>
                  <TableRow>
                    {columns.map(
                      (column) => (
                        <TableCell
                          key={
                            column
                          }
                          sx={{
                            fontWeight:
                              700,

                            fontSize:
                              ".82rem",

                            whiteSpace:
                              "nowrap",

                            backgroundColor:
                              "primary.main",

                            color:
                              "primary.contrastText",

                            py: 1.7,
                            px: 2,

                            "&.MuiTableCell-stickyHeader":
                              {
                                backgroundColor:
                                  "primary.main",

                                color:
                                  "primary.contrastText",
                              },
                          }}
                        >
                          {labelize(
                            column
                          )}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.map(
                    (
                      row,
                      index
                    ) => (
                      <TableRow
                        key={
                          row?.id ??
                          index
                        }
                        hover
                        sx={{
                          "&:nth-of-type(even)":
                            {
                              backgroundColor:
                                "action.hover",
                            },

                          "&:hover":
                            {
                              backgroundColor:
                                "action.selected",
                            },
                        }}
                      >
                        {columns.map(
                          (
                            column
                          ) => (
                            <TableCell
                              key={
                                column
                              }
                              sx={{
                                py: 1.5,
                                px: 2,
                                fontSize:
                                  ".84rem",
                                whiteSpace:
                                  "nowrap",
                                borderColor:
                                  "divider",
                                color:
                                  "text.primary",
                              }}
                            >
                              {display(
                                row,
                                column
                              )}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={
                filteredRows.length
              }
              page={page}
              onPageChange={(
                _,
                nextPage
              ) => {
                setPage(
                  nextPage
                );
              }}
              rowsPerPage={
                rowsPerPage
              }
              onRowsPerPageChange={(
                event
              ) => {
                setRowsPerPage(
                  parseInt(
                    event.target
                      .value,
                    10
                  )
                );

                setPage(0);
              }}
              rowsPerPageOptions={[
                10,
                25,
                50,
                100,
              ]}
              sx={{
                borderTop: 1,
                borderColor:
                  "divider",
                backgroundColor:
                  "background.paper",

                "& .MuiTablePagination-toolbar":
                  {
                    minHeight: 52,
                    px: {
                      xs: 1,
                      md: 2,
                    },
                  },
              }}
            />
          </Paper>
        )}
      </Box>

      {/* FILTER MODAL */}

      {filterModal}

      {/* PDF COLOR MODAL */}

      <ReportPdfColorModal
  open={colorOpen}
  onClose={() =>
          setColorOpen(false)}
  themeColor={tempThemeColor}
  setThemeColor={(hex) => { setTempThemeColor(
                    hex
                  ) }}
  textColor={tempTextColor}
  setTextColor={(hex) => { setTempTextColor(
                    hex
                  ) }}
  reportTitle="HR Report"
  onGenerate={() => {
                updatePdfThemeColor(
                  tempThemeColor
                );

                updatePdfTextColor(
                  tempTextColor
                );

                setColorOpen(
                  false
                );

                generatePDF(
                  tempThemeColor,
                  tempTextColor
                );
              }}
  loading={pdfLoading}
  disabled={pdfLoading}
/>

      {/* SNACKBAR */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          4500
        }
        onClose={() =>
          setSnackbar(
            (state) => ({
              ...state,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (state) => ({
                ...state,
                open: false,
              })
            )
          }
        >
          {
            snackbar.message
          }
        </Alert>
      </Snackbar>
    </Box>
  );
}